// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { addExam } from "../api";
import { ExamCardSectionNameType } from "src/constants/enums";
import { AddExamSectionType, AddLensSectionType } from "../types";

export const useAddExam = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      examData: { scheduledDate: string } & {
        [key in ExamCardSectionNameType]?: Array<
          AddExamSectionType | AddLensSectionType
        >;
      };
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return addExam(token, clinicId, params.patientId, params.examData);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exams"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-exams"] });
      queryClient.invalidateQueries({ queryKey: ["linked-exams"] });

      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Exam added successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Exam could not be added!"),
      });
    },
  });
};
